import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { shallow } from 'zustand/shallow'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useApiErrorMessage } from '../../i18n/useApiErrorMessage'

import Box from '@mui/material/Box'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import SearchBar from 'material-ui-search-bar'

import { MaterialIcons } from '../../icons/Material/MaterialIcons'

import Layout from '../Layout/Layout'
import DataTable from '../table/DataTable'
import ReintentarModal from '../Modals/ReintentarModal/ReintentarModal'

import {
  getCompanyNoveltiesByPeriodId,
  getCompanyPeriodsTypes,
} from '../../services/CompaniesService'
import { getCloserPeriods } from '../../services/ParametersService'

import { useEmpresaStore } from '../../store/empresaStore'
// import { useUsersStore } from "../../store/usersStore";

import { reorderPeriods } from '../../utils/reorderPeriods'

import {
  CompanyNoveltiesType,
  CompanyPeriodsType,
  TipoPeriodoRowsType,
} from '../../types'
import { useHasPermissions } from 'react-simple-rbac'
import HtmlTooltip from '../HtmlTooltip/HtmlTooltip'

import i18n from '../../i18n/config'
import { noveltyStatusI18nKey } from '../../i18n/useNoveltyStatusLabel'

//1: success, 2:pending, 3:cancel
const getUrgentIcon = (params: GridRenderCellParams) => {
  if (params.value) {
    return (
      <Box
        sx={{
          display: 'flex',
          placeItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <MaterialIcons.LightningBolt color="error" />
      </Box>
    )
  }
  return <></>
}

//1: success, 2:pending, 3:cancel
const getAction = (params: GridRenderCellParams) => {
  if (params.value === '2') {
    return (
      <Box
        sx={{
          display: 'flex',
          placeItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Button variant="contained" color="primary">
          {i18n.t('actions.processNow', { ns: 'common' })}
        </Button>
        <MaterialIcons.EditIcon />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        placeItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
      }}
    >
      <MaterialIcons.EditIcon />
    </Box>
  )
}

//0: Active, 1:Inactive, 2:Cancel, 3:Block, 4:Error
const getStatus = (params: GridRenderCellParams) => {
  const i18nKey = noveltyStatusI18nKey(params.value)
  return (
    <Box
      key={params.value}
      sx={{
        display: 'flex',
        placeItems: 'center',
        width: '100%',
      }}
    >
      {/* <MaterialIcons.BriefcaseCheck color="success" fontSize="small" /> */}
      <Typography marginLeft="8px">
        {i18nKey ? i18n.t(i18nKey, { ns: 'status' }) : params.value}
      </Typography>
    </Box>
  )
}
const Novedades = () => {
  const { t } = useTranslation(['novedades', 'common'])
  const getApiErrorMessage = useApiErrorMessage()
  const [searched, setSearched] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [openReintentarModal, setOpenReintentarModal] = useState<boolean>(false)
  const [retryGetPeriodos, setRetryGetPeriodos] = useState<boolean>(false)
  const [novelties, setNovelties] = useState<CompanyNoveltiesType[]>([])
  const [originalNovelties, setOriginalNovelties] = useState<CompanyNoveltiesType[]>([])
  const [empresaPeriodos, setEmpresaPeriodos] = useState<CompanyPeriodsType[]>([])
  const [empresaPeriodo, setEmpresaPeriodo] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()

  const { empresaId, returnEmpresaPeriodos } = useEmpresaStore(
    ({ empresaId, returnEmpresaPeriodos }) => ({ empresaId, returnEmpresaPeriodos }),
    shallow,
  )
  const canCreateNovelty = useHasPermissions(['create:novelty'])

  const requestSearch = (searchedVal: string) => {
    if (searchedVal.length > 2) {
      const filteredRows = originalNovelties.filter(row =>
        row.name.toLowerCase().includes(searchedVal.toLowerCase()),
      )
      setNovelties(filteredRows)
    } else {
      setNovelties(originalNovelties)
    }
  }

  const cancelSearch = () => {
    setSearched('')
    requestSearch(searched)
  }

  const crearNovedad = () => {
    navigate('crearnovedad')
  }

  const getPeriodo = (params: GridRenderCellParams) => {
    const periodos = [...returnEmpresaPeriodos()]
    if (params.value) {
      const periodo = periodos.find(period => period.id === params.value)!
      return periodo ? periodo.name : 'no'
    }
  }

  const handleChangePeriod = (periodId: string) => {
    setEmpresaPeriodo(periodId)
    getNoveltiesByPeriodId(periodId)
  }

  const handleOpenDialog = () => setOpenReintentarModal(true)
  const handleCloseDialog = () => setOpenReintentarModal(false)
  const handleConfirmAction = () => {
    setRetryGetPeriodos(true)
    setOpenReintentarModal(false)
  }

  const getSanitizedPeriodsByCompanyPeriodTypes = (
    periodTypes: TipoPeriodoRowsType[],
    periods: CompanyPeriodsType[],
  ): CompanyPeriodsType[] => {
    // Crear un Set de IDs de periodTypes activos para búsqueda eficiente
    const activePeriodTypeIds = new Set(periodTypes.map(pt => pt.id))

    // Filtrar períodos que tengan un periodType activo en la empresa
    return periods.filter(period => activePeriodTypeIds.has(period.period_type_id))
  }

  const getCloserPeriod = (unOrderedPeriods: CompanyPeriodsType[]) => {
    if (unOrderedPeriods.length > 0) {
      const sortedPeriods = [...reorderPeriods([...unOrderedPeriods])]
      setEmpresaPeriodos(sortedPeriods)
      setEmpresaPeriodo(sortedPeriods[0].id)
      getNoveltiesByPeriodId(sortedPeriods[0].id)
    } else {
      setIsLoading(false)
      setEmpresaPeriodos([])
      setNovelties([])
      setOriginalNovelties([])
      setEmpresaPeriodo('')
    }
  }

  const getNoveltiesByPeriodId = useCallback(
    async (periodId: string) => {
      setIsLoading(true)
      const token = await getAccessTokenSilently()
      getCompanyNoveltiesByPeriodId(token, empresaId, periodId)
        .then(result => {
          setErrorMessage('')
          setNovelties(result.data)
          setOriginalNovelties(result.data)
          setIsLoading(false)
        })
        .catch(e => {
          console.error('error', e.response?.data?.error)
          setErrorMessage(getApiErrorMessage(e))
          setNovelties([])
          setOriginalNovelties([])
          setIsLoading(false)
        })
    },
    [empresaId, getAccessTokenSilently],
  )

  const getPeridosCercanos = useCallback(async () => {
    setIsLoading(true)
    const token = await getAccessTokenSilently()
    try {
      const [periodsResult, periodTypesResult] = await Promise.all([
        getCloserPeriods(token, empresaId),
        getCompanyPeriodsTypes(token, empresaId),
      ])
      const sanitizedPeriods = getSanitizedPeriodsByCompanyPeriodTypes(
        periodTypesResult.data,
        periodsResult.data,
      )
      getCloserPeriod(sanitizedPeriods)
    } catch (e: any) {
      setIsLoading(false)
      setEmpresaPeriodos([])
      setEmpresaPeriodo('')
      setNovelties([])
      setOriginalNovelties([])
      if (e.request?.status === 0) {
        setErrorMessage(t('errors.cors', { ns: 'common' }))
        handleOpenDialog()
      } else {
        setErrorMessage(t('errors.generic', { ns: 'common' }))
      }
      console.error('ERROR', e)
    }
  }, [empresaId, getAccessTokenSilently])

  useEffect(() => {
    getPeridosCercanos()
  }, [getPeridosCercanos])

  useEffect(() => {
    if (retryGetPeriodos) {
      getPeridosCercanos()
      setRetryGetPeriodos(false)
    }
  }, [getPeridosCercanos, retryGetPeriodos])

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('list.columns.name', { ns: 'novedades' }),
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'period_id',
      headerName: t('list.columns.period', { ns: 'novedades' }),
      headerClassName: 'theme--header',
      flex: 1,
      renderCell: getPeriodo,
    },
    {
      field: 'created_at',
      headerName: t('list.columns.createdAt', { ns: 'novedades' }),
      type: 'dateTime',
      headerClassName: 'theme--header',
      flex: 1,
      valueFormatter: ({ value }) => value && dayjs(value).format('DD/MM/YYYY'),
    },
    {
      field: 'process_date',
      headerName: t('list.columns.processDate', { ns: 'novedades' }),
      type: 'dateTime',
      headerClassName: 'theme--header',
      flex: 1,
      valueFormatter: ({ value }) => value && dayjs(value).format('DD/MM/YYYY'),
    },
    {
      field: 'user',
      headerName: t('list.columns.user', { ns: 'novedades' }),
      headerClassName: 'theme--header',
      flex: 1,
      renderCell: params => params.value.email,
    },
    {
      field: 'urgent',
      headerName: t('list.columns.urgent', { ns: 'novedades' }),
      headerClassName: 'theme--header',
      renderCell: getUrgentIcon,
      flex: 1,
    },
    {
      field: 'status',
      headerName: t('list.columns.status', { ns: 'novedades' }),
      type: 'number',
      headerClassName: 'theme--header',
      renderCell: getStatus,
      flex: 1,
      headerAlign: 'left',
    },
    {
      field: 'action',
      headerName: t('list.columns.action', { ns: 'novedades' }),
      type: 'number',
      headerClassName: 'theme--header',
      renderCell: getAction,
      flex: 1,
    },
  ]

  return (
    <Layout icon="PollIcon" title={t('list.breadcrumb', { ns: 'novedades' })}>
      <Box sx={{ height: '100%', '& .theme--header': { backgroundColor: '#EEF1FC' } }}>
        <Grid container spacing={2} sx={{ py: '24px', placeItems: 'center' }}>
          <Grid display="flex" sx={{ placeItems: 'center' }} item xs={6}>
            <Box sx={{ marginRight: 4, width: '459px' }}>
              <SearchBar
                value={searched}
                onChange={searchVal => requestSearch(searchVal)}
                onCancelSearch={() => cancelSearch()}
                placeholder={t('list.searchPlaceholder', { ns: 'novedades' })}
                style={{ boxShadow: 'none' }}
              />
            </Box>
            <Box sx={{ display: 'flex', placeItems: 'center' }}>
              <Typography
                sx={{
                  color: 'black',
                  fontSize: '18px',
                  fontWeight: 500,
                  marginRight: '23px',
                }}
              >
                {t('list.periodLabel', { ns: 'novedades' })}
              </Typography>
              <TextField
                id="periodo-select"
                select
                fullWidth
                name="period_id"
                onChange={event => handleChangePeriod(event.target.value)}
                value={
                  isLoading
                    ? 'loading'
                    : empresaPeriodos.length === 0
                      ? 'empty'
                      : empresaPeriodo
                }
                variant="filled"
                disabled={isLoading || empresaPeriodos.length === 0}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    '.MuiInputBase-input': {
                      paddingY: '12px',
                      display: 'flex',
                      justifyContent: 'flex-start',
                    },
                  },
                }}
                helperText={
                  isLoading
                    ? t('crear.loadingPeriods', { ns: 'novedades' })
                    : empresaPeriodos.length === 0 &&
                      t('crear.noPeriodsActive', { ns: 'novedades' })
                }
              >
                {!isLoading &&
                  empresaPeriodos.length > 0 &&
                  empresaPeriodos.map(periodo => (
                    <MenuItem key={periodo.id} value={periodo.id}>
                      {periodo.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Grid container display="flex" justifyContent="flex-end">
              <Stack direction="row" spacing={2}>
                {canCreateNovelty ? (
                  <Button
                    variant="contained"
                    startIcon={<MaterialIcons.AddchartIcon />}
                    color="primary"
                    onClick={() => crearNovedad()}
                  >
                    {t('list.createButton', { ns: 'novedades' })}
                  </Button>
                ) : (
                  <HtmlTooltip
                    title={<b>{t('list.noCreatePermission', { ns: 'novedades' })}</b>}
                  >
                    <span>
                      <Button variant="contained" color="primary" disabled>
                        {t('list.createButton', { ns: 'novedades' })}
                      </Button>
                    </span>
                  </HtmlTooltip>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <DataTable
          columns={columns}
          rows={novelties}
          navigateRowLink="/novedades/novedad"
          noFilterRowsMessage={t('list.noFilterRowsMessage', { ns: 'novedades' })}
          noRowsMessage={errorMessage || t('list.noRowsMessage', { ns: 'novedades' })}
          loading={isLoading}
          sortModel={[{ field: 'created_at', sort: 'desc' }]}
        />
      </Box>
      {openReintentarModal && (
        <ReintentarModal
          open={openReintentarModal}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmAction}
        />
      )}
    </Layout>
  )
}

export default Novedades
